import { AlertCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'

import {
  Badge,
  Divider,
  EmptyState,
  Heading,
  Loading,
  Switch,
  TabContent,
  TabItem,
  Tabs,
  Text,
} from '@ankor-io/ui-components'

import ConnectedAccountCards, {
  type CardsTableConnectedAccount,
} from '@/components/organizations/vessels/connected-account-cards'
import OrganizationVesselsAccounts, {
  VESSEL_CARDS_SECTION_ID,
} from '@/components/organizations/vessels/organization-vessels-accounts'
import { useOrganizationDetailsQuery } from '@/queries/organization'
import { getOrganizationDisplayDescription } from '@/utils/organization'

export function OrganizationVessels() {
  const { id } = useParams<{ id: string }>()
  const { data: organization, error, isPending } = useOrganizationDetailsQuery(id)
  const [selectedVesselId, setSelectedVesselId] = useState<string | null>(null)
  const [selectedConnectedAccount, setSelectedConnectedAccount] = useState<CardsTableConnectedAccount | null>(null)
  const cardsHeadingRef = useRef<HTMLHeadingElement>(null)
  // Error toast is handled globally by QueryProvider
  // Component still handles error UI state (EmptyState below)

  const handleConnectedAccountClick = (vesselId: string, connectedAccount: CardsTableConnectedAccount) => {
    // Toggle the section closed when the already-selected account is clicked again.
    if (vesselId === selectedVesselId && connectedAccount.id === selectedConnectedAccount?.id) {
      setSelectedVesselId(null)
      setSelectedConnectedAccount(null)
      return
    }
    setSelectedVesselId(vesselId)
    setSelectedConnectedAccount(connectedAccount)
  }

  // The cards section renders inline below a long, scrolling vessel list. Without this it lands
  // far below the fold with no feedback, so a click looks like it did nothing. Bring its heading
  // into view and move focus to it when an account is selected.
  useEffect(() => {
    if (selectedConnectedAccount && selectedVesselId) {
      cardsHeadingRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
      cardsHeadingRef.current?.focus?.({ preventScroll: true })
    }
  }, [selectedConnectedAccount?.id, selectedVesselId])

  if (!id) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Text className="text-red-600">Organization ID is required</Text>
      </div>
    )
  }

  if (isPending) {
    return <Loading variant="spinner" />
  }

  if (error) {
    return (
      <EmptyState
        heading="Failed to load organization"
        subheading={error?.message || 'An error occurred while loading organization details.'}
        icon={<AlertCircle className="mx-auto size-12 text-gray-400" />}
      />
    )
  }

  return (
    <div className="space-y-4 relative min-h-[calc(100vh-7.5rem)] pb-10">
      <div className="space-y-4">
        <Heading level={2}>{organization?.name}</Heading>

        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">Id:</span>
            <Badge color="gray">{organization?.id}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">Users:</span>
            <Badge color="gray">{organization?.userCount || 0}</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-900 dark:text-white">Visibility:</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {organization?.visibility === 'visible' ? 'Visible' : 'Hidden'}
              </span>
              <Switch
                checked={organization?.visibility === 'visible'}
                disabled
              />
            </div>
          </div>
        </div>

        <Text className="max-w-2xl text-gray-600 dark:text-gray-400">
          {getOrganizationDisplayDescription(organization)}
        </Text>

        <Divider />
      </div>

      <Tabs>
        <TabItem
          current={true}
          to={`/organizations/${id}/vessels`}
        >
          Vessels
        </TabItem>
        <TabItem
          current={false}
          to={`/organizations/${id}`}
        >
          Feature Flags
        </TabItem>
        <TabItem
          current={false}
          to={`/organizations/${id}/pricing`}
        >
          Pricing
        </TabItem>
        <TabItem
          current={false}
          to={`/organizations/${id}/integrations`}
        >
          Integrations
        </TabItem>
        <TabItem
          current={false}
          to={`/organizations/${id}/pipelines`}
        >
          Pipelines
        </TabItem>
      </Tabs>

      <TabContent isActive={true}>
        <OrganizationVesselsAccounts
          organizationId={id}
          onConnectedAccountClick={handleConnectedAccountClick}
          selectedVesselId={selectedVesselId}
          selectedConnectedAccountId={selectedConnectedAccount?.id ?? null}
        />
        {selectedConnectedAccount && selectedVesselId && (
          <div
            id={VESSEL_CARDS_SECTION_ID}
            role="region"
            aria-label="Cards"
          >
            <ConnectedAccountCards
              orgId={id}
              vesselId={selectedVesselId}
              connectedAccount={selectedConnectedAccount}
              headingRef={cardsHeadingRef}
            />
          </div>
        )}
      </TabContent>
    </div>
  )
}

export default OrganizationVessels
